import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDbUser } from '../hooks/useDbUser';
import { db, Task, MatterDeadline } from '../lib/db';
import { 
  FolderOpen, 
  Folder, 
  Target, 
  CheckSquare, 
  RotateCw,
  Calendar,
  AlertCircle,
  Clock,
  User,
  Flag,
  Pause,
  HelpCircle,
  PlayCircle,
  CheckCircle,
  XCircle,
  GitBranch,
  ParkingCircle,
  Zap
} from 'lucide-react';
import { 
  DeadlineSummaryCard, 
  DeadlineWidget,
  DeadlineCalendarWidget 
} from '../components/dashboard';

interface TaskGroup {
  type: string;
  tasks: Task[];
}

function OverviewPage() {
  const { dbUserId, loading: dbUserLoading } = useDbUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Counts
  const [projectCount, setProjectCount] = useState(0);
  const [matterCount, setMatterCount] = useState(0);
  const [deliverableCount, setDeliverableCount] = useState(0);
  const [liveTaskCount, setLiveTaskCount] = useState(0);
  const [regularTaskCount, setRegularTaskCount] = useState(0);
  
  // Task lists
  const [criticalLiveTasks, setCriticalLiveTasks] = useState<Task[]>([]);
  const [overdueTasks, setOverdueTasks] = useState<TaskGroup[]>([]);
  
  // Deadline state
  const [deadlines, setDeadlines] = useState<MatterDeadline[]>([]);
  const [deadlineStats, setDeadlineStats] = useState({
    overdue: 0,
    today: 0,
    thisWeek: 0,
    upcoming30Days: 0
  });

  useEffect(() => {
    if (dbUserId && !dbUserLoading) {
      loadDashboardData();
    }
  }, [dbUserId, dbUserLoading]);

  const loadDashboardData = async () => {
    if (!dbUserId) return;
    
    setLoading(true);
    try {
      // Load counts
      const [projects, matters, deliverables, tasks, todaysRegularTasks, overdueRegularTasks, upcomingDeadlines, deadlineStatsData] = await Promise.all([
        db.projects.findByUserId(dbUserId),
        db.matters.findByUserId(dbUserId),
        db.goals.findByUserId(dbUserId, false), // Active deliverables only
        db.tasks.findByUserId(dbUserId),
        db.tasks.findTodaysRegularTasks(dbUserId),
        db.tasks.findOverdueRegularTasks(dbUserId),
        db.deadlines.getUpcoming(dbUserId, 30),
        db.deadlines.getStats(dbUserId)
      ]);

      setProjectCount(projects.length);
      setMatterCount(matters.length);
      setDeliverableCount(deliverables.length);
      
      // Filter out regular tasks from the general task count
      const nonRegularTasks = tasks.filter(t => !t.is_regular);
      setLiveTaskCount(nonRegularTasks.filter(t => t.is_live && t.status !== 'Complete').length);
      setRegularTaskCount(todaysRegularTasks.length + overdueRegularTasks.length);

      // Get today's date range
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Get all live tasks (excluding regular tasks from general list, but including today's regular tasks)
      const allLiveTasks = [...nonRegularTasks, ...todaysRegularTasks].filter(t => {
        // Regular tasks don't use is_live field, they're already filtered by date
        if (t.is_regular) {
          return t.status !== 'Complete' && t.status !== 'Superseded';
        }
        // Non-regular tasks must be live
        return t.is_live && t.status !== 'Complete' && t.status !== 'Superseded';
      });
      
      // Sort by priority and take top 10
      const priorityOrder = { 'Critical': 0, 'High': 1, 'Medium': 2, 'Low': 3 };
      const sortedLiveTasks = allLiveTasks.sort((a, b) => {
        const aPriority = priorityOrder[a.priority || 'Low'];
        const bPriority = priorityOrder[b.priority || 'Low'];
        return aPriority - bPriority;
      }).slice(0, 10);
      
      setCriticalLiveTasks(sortedLiveTasks);

      // Filter overdue tasks (excluding regular tasks from general list)
      const activeNonRegularTasks = nonRegularTasks.filter(t => t.status !== 'Complete' && t.status !== 'Superseded');
      const overdueNormalTasks = activeNonRegularTasks.filter(task => {
        if (!task.deadline) return false;
        const deadline = new Date(task.deadline);
        return deadline < today;
      });

      // Combine all overdue tasks (normal + regular)
      const allOverdueTasks = [...overdueNormalTasks, ...overdueRegularTasks];

      // Group by task type
      setOverdueTasks(groupTasksByType(allOverdueTasks));
      
      // Set deadline data
      setDeadlines(upcomingDeadlines);
      setDeadlineStats(deadlineStatsData);
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupTasksByType = (tasks: Task[]): TaskGroup[] => {
    const grouped = tasks.reduce((acc, task) => {
      const type = task.task_type || 'To Do';
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(task);
      return acc;
    }, {} as Record<string, Task[]>);

    return Object.entries(grouped)
      .map(([type, tasks]) => ({ type, tasks }))
      .sort((a, b) => a.type.localeCompare(b.type));
  };

  const getTaskTypeIcon = (type: string) => {
    switch (type) {
      case 'To Do': return <CheckSquare className="w-4 h-4" />;
      case 'Waiting': return <Clock className="w-4 h-4" />;
      case 'Chasing': return <User className="w-4 h-4" />;
      case 'In Progress': return <PlayCircle className="w-4 h-4" />;
      case 'Done': return <CheckCircle className="w-4 h-4" />;
      case 'Parked': return <ParkingCircle className="w-4 h-4" />;
      default: return <HelpCircle className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority: string | undefined) => {
    switch (priority) {
      case 'Critical': return 'text-red-700 bg-red-50';
      case 'High': return 'text-orange-700 bg-orange-50';
      case 'Medium': return 'text-yellow-700 bg-yellow-50';
      case 'Low': return 'text-green-700 bg-green-50';
      default: return 'text-gray-700 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string | undefined) => {
    switch (status) {
      case 'To Do': return <CheckSquare className="w-4 h-4" />;
      case 'Waiting': return <Pause className="w-4 h-4" />;
      case 'Delegated': return <User className="w-4 h-4" />;
      case 'In Progress': return <PlayCircle className="w-4 h-4" />;
      case 'Complete': return <CheckCircle className="w-4 h-4" />;
      case 'Stuck': return <AlertCircle className="w-4 h-4" />;
      case 'Failed': return <XCircle className="w-4 h-4" />;
      case 'Superseded': return <GitBranch className="w-4 h-4" />;
      default: return <HelpCircle className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <button
          onClick={() => navigate('/focus')}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
        >
          <Zap className="w-4 h-4 mr-2" />
          Focus Mode
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-8">
        <div 
          className="bg-white rounded-lg shadow p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/projects')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Projects</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{projectCount}</p>
            </div>
            <FolderOpen className="w-6 sm:w-8 h-6 sm:h-8 text-indigo-500" />
          </div>
        </div>

        <div 
          className="bg-white rounded-lg shadow p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/matters')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Matters</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{matterCount}</p>
            </div>
            <Folder className="w-6 sm:w-8 h-6 sm:h-8 text-purple-500" />
          </div>
        </div>

        <div 
          className="bg-white rounded-lg shadow p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/dashboard')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Deliverables</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{deliverableCount}</p>
            </div>
            <Target className="w-6 sm:w-8 h-6 sm:h-8 text-green-500" />
          </div>
        </div>

        <div 
          className="bg-white rounded-lg shadow p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/focus')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Live Tasks</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{liveTaskCount}</p>
            </div>
            <CheckSquare className="w-6 sm:w-8 h-6 sm:h-8 text-blue-500" />
          </div>
        </div>

        <div 
          className="bg-white rounded-lg shadow p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/daily')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Regular Tasks Today</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{regularTaskCount}</p>
            </div>
            <RotateCw className="w-6 sm:w-8 h-6 sm:h-8 text-teal-500" />
          </div>
        </div>
      </div>

      {/* Deadlines Section */}
      <div className="mb-8">
        <DeadlineSummaryCard 
          stats={deadlineStats}
          onStatClick={(stat) => {
            // Navigate to deadlines page with filter
            navigate('/deadlines', { state: { filter: stat } });
          }}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Live Tasks - Second on mobile, first on desktop */}
        <div className="bg-white rounded-lg shadow order-2 lg:order-1">
          <div 
            className="px-6 py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
            onClick={() => navigate('/focus')}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Live Tasks</h2>
              <CheckSquare className="w-5 h-5 text-green-500" />
            </div>
          </div>
          <div className="px-6 py-4">
            {criticalLiveTasks.length === 0 ? (
              <p className="text-gray-500 text-sm">No live tasks</p>
            ) : (
              <div className="space-y-3">
                {criticalLiveTasks.map((task) => (
                  <div key={task.id} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getStatusIcon(task.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">{task.title}</p>
                      {task.description && (
                        <p className="text-xs text-gray-500 truncate mt-1">{task.description}</p>
                      )}
                      <div className="flex items-center mt-1 space-x-2">
                        {task.priority && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(task.priority)}`}>
                            <Flag className="w-3 h-3 mr-1" />
                            {task.priority}
                          </span>
                        )}
                        {task.is_regular && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-teal-700 bg-teal-50">
                            <RotateCw className="w-3 h-3 mr-1" />
                            {task.frequency || 'Regular'}
                          </span>
                        )}
                        {task.deadline && (
                          <span className="inline-flex items-center text-xs text-gray-500">
                            <Calendar className="w-3 h-3 mr-1" />
                            Due {new Date(task.deadline).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Overdue Tasks - First on mobile, second on desktop */}
        <div className="bg-white rounded-lg shadow order-1 lg:order-2">
          <div 
            className="px-6 py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
            onClick={() => navigate('/focus')}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Overdue Tasks</h2>
              <AlertCircle className="w-5 h-5 text-red-500" />
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {overdueTasks.length === 0 ? (
              <p className="px-6 py-4 text-gray-500 text-sm">No overdue tasks</p>
            ) : (
              overdueTasks.map((group) => (
                <div key={group.type} className="px-6 py-4">
                  <div className="flex items-center mb-3">
                    {getTaskTypeIcon(group.type)}
                    <h3 className="ml-2 text-sm font-medium text-gray-900">
                      {group.type} ({group.tasks.length})
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {group.tasks.map((task) => {
                      const daysOverdue = task.deadline 
                        ? Math.floor((new Date().getTime() - new Date(task.deadline).getTime()) / (1000 * 60 * 60 * 24))
                        : 0;
                      
                      return (
                        <div key={task.id} className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-0.5">
                            {getStatusIcon(task.status)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 truncate">{task.title}</p>
                            {task.description && (
                              <p className="text-xs text-gray-500 truncate mt-1">{task.description}</p>
                            )}
                            <div className="flex items-center mt-1 space-x-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-red-700 bg-red-50">
                                {daysOverdue} day{daysOverdue !== 1 ? 's' : ''} overdue
                              </span>
                              {task.priority && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(task.priority)}`}>
                                  <Flag className="w-3 h-3 mr-1" />
                                  {task.priority}
                                </span>
                              )}
                              {task.is_regular && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-teal-700 bg-teal-50">
                                  <RotateCw className="w-3 h-3 mr-1" />
                                  {task.frequency || 'Regular'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Upcoming Deadlines - Third column */}
        <div className="bg-white rounded-lg shadow order-3">
          <DeadlineWidget 
            deadlines={deadlines.slice(0, 5)}
            title="Deadlines"
            emptyMessage="No deadlines"
            onDeadlineClick={(deadline) => {
              navigate('/matters', { state: { openMatterId: deadline.matter_id, highlightDeadline: deadline.id } });
            }}
            onViewAll={() => navigate('/deadlines')}
          />
        </div>
      </div>

      {/* Calendar View */}
      <div className="mt-8">
        <DeadlineCalendarWidget 
          deadlines={deadlines}
          onDeadlineClick={(deadline) => {
            navigate('/matters', { state: { openMatterId: deadline.matter_id, highlightDeadline: deadline.id } });
          }}
        />
      </div>
    </div>
  );
}

export default OverviewPage;